<template>
    <component
        :aria-label="iconAriaLabel"
        :is="componentType"
        :class="[$style['mds-button'], classObject, variationClass, sizeClass]"
        :href="href"
        v-bind="routingProps"
        v-on="$listeners"
        :data-mds-version="version"
    >
        <mds-icon
            v-if="icon"
            :class="[$style['mds-button__icon'], $style['mds-button__icon--left']]"
            :name="icon"
            :size="iconSize"
        ></mds-icon>
        <span v-if="variation !== 'icon-only'">
            <slot>{{ text }}</slot>
        </span>
        <mds-icon
            v-if="iconRight"
            :class="[$style['mds-button__icon'], $style['mds-button__icon--right']]"
            :name="iconRight"
            :size="iconSize"
        ></mds-icon>
        <transition
            :enter-active-class="$style['mds-button__loader-enter-active']"
            :leave-active-class="$style['mds-button__loader-leave-active']"
            :enter-class="$style['mds-button__loader-enter']"
            :leave-to-class="$style['mds-button__loader-leave-to']"
        >
            <mds-loader
                v-if="loading && variation === 'primary'"
                :class="[$style['mds-button__loader']]"
                :aria-valuetext="loaderAriaValuetext"
                on-dark
            />
        </transition>
    </component>
</template>

<script>
    import MdsIcon from '@mds/icon';
    import MdsRoutable from '@mds/routable';
    import MdsLoader from '@mds/loader';
    import packageInfo from '../package.json';
    const { name, version } = packageInfo;

    export default {
        name: 'MdsButton',
        components: {
            MdsIcon,
            MdsLoader,
        },
        mixins: [MdsRoutable],
        props: {
            href: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'When provided, makes the button `a`-based and sets the `href` path.',
                },
            },
            flatButtonIconSize: {
                type: String,
                default: 'default',
                validator(value) {
                    return ['default', 'small', 'medium'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'Button variation must be `flat`. One of: `default`, `small`, `medium`.',
                    description: 'Sets the size of icons within a flat button.',
                },
            },
            icon: {
                type: String,
                docs: {
                    validation: 'Must be a valid icon name.',
                    description: 'Sets the left icon.',
                },
            },
            iconRight: {
                type: String,
                docs: {
                    validation: 'Must be a valid icon name.',
                    description: 'Sets the right icon.',
                },
            },
            loaderAriaValuetext: {
                type: String,
                default: 'Loading...',
                docs: {
                    validation: '—',
                    description: 'Sets the loader `aria-valuetext` attribute.',
                },
            },
            loading: {
                type: Boolean,
                default: false,
                docs: {
                    validation: 'Button variation must be `primary`.',
                    description: 'If `true`, hides the button content and displays a loader in its place.',
                },
            },
            onDark: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, changes component to on-dark styling.',
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium', 'large', 'touch'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `small`, `medium`, `large`, `touch`.',
                    description: 'Sets the size of the button.',
                },
            },
            text: {
                type: String,
                docs: {
                    validation: '**Required**',
                    description: 'Sets the button text.',
                },
            },
            variation: {
                type: String,
                default: 'secondary',
                validator(value) {
                    return ['primary', 'secondary', 'flat', 'icon-only'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `primary`, `secondary`, `flat`, `icon-only`.',
                    description: 'Sets the button variation.',
                },
            },
            version: {
                type: String,
                default: `${name}-${version}`,
                docs: {
                    validation: 'Read-only',
                    description:
                        'Set automatically by package version, do not manually adjust this prop. Used to add a data attribute providing the component version to the component markup rendered in the DOM.',
                },
            },
        },
        computed: {
            componentType() {
                return this.isRoutable ? this.linkTag : this.href ? 'a' : 'button';
            },
            sizeClass() {
                return this.$style[`mds-button--${this.size}`];
            },
            classObject() {
                const obj = {};
                obj[this.$style['mds-component--on-dark']] = this.onDark;
                obj[this.$style['mds-button--flat-m-icon']] = this.mediumIcon;
                obj[this.$style['mds-button--loading']] = this.loading && this.variation === 'primary';

                return obj;
            },
            iconSize() {
                const isSmall =
                    this.size === 'small' ||
                    (this.size === 'medium' &&
                        this.variation !== 'icon-only' &&
                        this.flatButtonIconSize !== 'medium') ||
                    this.flatButtonIconSize === 'small';
                return isSmall ? 'small' : 'medium';
            },
            variationClass() {
                return this.$style[`mds-button--${this.variation}`];
            },
            iconAriaLabel() {
                let buttonText = this.text;
                if (this.$slots.default) {
                    buttonText = this.$slots.default[0].text;
                }
                return this.variation === 'icon-only' ? buttonText : null;
            },
        },
        data() {
            return {
                mediumIcon: false,
            };
        },
        mounted() {
            this.mediumIcon = this.iconSize === 'medium' && this.variation === 'flat' && this.size === 'medium';
        },
    };
</script>

<style lang="scss" module>
    @import './button.scss';
</style>
